import { computed } from '@angular/core';
import type { RequestStatusState } from '@indutrust/shared-model';
import { signalStoreFeature, withComputed, withState } from '@ngrx/signals';

/**
 * Add request-status state and derived selectors to an NgRx Signal Store.
 *
 * Provides the `requestStatus` state with helpers: `isLoading`, `isSuccess`, and `error`.
 *
 * @returns A Signal Store feature that can be composed with other features.
 * @example
 * const feature = withRequestStatus();
 */
export function withRequestStatus() {
  return signalStoreFeature(
    withState<RequestStatusState>({ requestStatus: 'initial' }),
    withComputed(({ requestStatus }) => ({
      isLoading: computed(() => requestStatus() === 'loading'),
      isSuccess: computed(() => requestStatus() === 'success'),
      error: computed(() => {
        const status = requestStatus();
        return typeof status === 'object' ? status.error : null;
      }),
    }))
  );
}

/**
 * Helper to set the store's request status to the `'loading'` state.
 *
 * @returns A partial state object to be patched in the store.
 * @example
 * store.patchState(setLoading());
 */
export function setLoading(): RequestStatusState {
  return { requestStatus: 'loading' };
}

/**
 * Helper to set the store's request status to the `'success'` state.
 *
 * @returns A partial state object to be patched in the store.
 * @example
 * store.patchState(setSuccess());
 */
export function setSuccess(): RequestStatusState {
  return { requestStatus: 'success' };
}

/**
 * Helper to set the store's request status to an error with message.
 *
 * @param error The error message to attach to the state.
 * @returns A partial state object to be patched in the store.
 * @example
 * store.patchState(setError('Network unreachable'));
 */
export function setError(error: string): RequestStatusState {
  return { requestStatus: { error } };
}
